import * as fabric from 'fabric';

/**
 * Per-instance dataURL cache for paint-surface FabricImages.
 *
 * Fabric's default `FabricImage.toObject` calls `this.getSrc()`, which for a
 * canvas-backed image runs `canvas.toDataURL()` — a synchronous, base64 PNG
 * encode of the entire surface. On a 4K paint surface that's 50–200ms of
 * main-thread work. We call `toObject` from three hot paths:
 *
 *   - `useCanvasHistory.saveSnapshot` (on every brush / retouch commit)
 *   - `useCanvasHistory.manualSnapshot` (user-pinned history entry)
 *   - `EditorPage.buildSnapshot` (debounced IDB session save)
 *
 * Each call walks every FabricImage and re-encodes every paint surface from
 * scratch — even surfaces that haven't changed. With N paint-surface layers
 * that's N × encode of the same untouched bytes.
 *
 * This installer attaches a per-instance `getSrc` override that returns a
 * cached dataURL on hit, and a `setElement` override that invalidates the
 * cache. Every paint write site already calls
 * `surface.fabricImg.setElement(surface.canvas)` after mutating pixels (the
 * conventional Fabric "I just changed the underlying element, please reset
 * caches" signal), so invalidation is automatic — no per-write-site edit is
 * needed for the cache to stay coherent.
 *
 * Install once at paint-surface creation (`ensurePaintSurface`) and once per
 * FabricImage during post-`loadFromJSON` rehydration (Fabric constructs fresh
 * instances on load, so the prior install is gone).
 *
 * Idempotent — guarded by `__fiasPaintSurfaceSerializer` so re-walks don't
 * double-wrap the methods.
 */

const INSTALL_FLAG = '__fiasPaintSurfaceSerializer';
const CACHE_KEY = '__fiasPaintSurfaceSrcCache';

type Cached = fabric.FabricImage & {
  [INSTALL_FLAG]?: boolean;
  [CACHE_KEY]?: string | undefined;
};

export function installPaintSurfaceSerializer(img: fabric.FabricImage): void {
  const tagged = img as Cached;
  if (tagged[INSTALL_FLAG]) return;

  const baseGetSrc = img.getSrc.bind(img) as (filtered?: boolean) => string;
  const baseSetElement = img.setElement.bind(img) as (
    element: HTMLImageElement | HTMLCanvasElement,
    size?: { width: number; height: number },
  ) => void;

  img.getSrc = function (this: fabric.FabricImage, filtered?: boolean): string {
    const self = this as Cached;
    // Bypass cache for filtered reads — Fabric's `getSrc(true)` returns the
    // post-filter element. Paint surfaces don't use filters today, but if
    // one ever gets attached, an unfiltered cache would silently serve the
    // wrong bytes. Cheap escape hatch.
    if (filtered) return baseGetSrc(filtered);
    if (self.filters && self.filters.length > 0) return baseGetSrc(filtered);
    const cached = self[CACHE_KEY];
    if (cached !== undefined) return cached;
    const fresh = baseGetSrc(filtered);
    self[CACHE_KEY] = fresh;
    return fresh;
  } as typeof img.getSrc;

  img.setElement = function (
    this: fabric.FabricImage,
    element: HTMLImageElement | HTMLCanvasElement,
    size?: { width: number; height: number },
  ): void {
    (this as Cached)[CACHE_KEY] = undefined;
    return baseSetElement(element, size);
  } as typeof img.setElement;

  tagged[INSTALL_FLAG] = true;
}

/**
 * Explicit cache invalidation. Not needed in the common case — the
 * `setElement` override handles it automatically wherever paint writes
 * follow the convention. Exposed for code paths that mutate pixels without
 * calling `setElement` (none today, but a defensive hook for future
 * additions).
 */
export function invalidatePaintSurfaceSrcCache(img: fabric.FabricImage): void {
  (img as Cached)[CACHE_KEY] = undefined;
}

/**
 * Per-instance dataURL cache for mask-clipPath FabricImages.
 *
 * Masks have a raw/effective split: RAW holds the user's unfiltered paint, and
 * EFFECTIVE is RAW with feather / invert / density baked in. Live rendering
 * uses EFFECTIVE; serialization must store RAW so that toggling those config
 * fields back to default after restore can recover the original edges.
 *
 * The previous implementation overrode `toObject` with a swap-element trick:
 * swap `_element` to RAW, call base `toObject` (which dataURLs the current
 * element), restore EFFECTIVE. That worked but still paid the
 * `canvas.toDataURL()` cost on every serialize. With a 200-step history +
 * 800ms-debounced IDB save, an unchanged mask was re-encoded dozens of times
 * per editing session for no reason.
 *
 * Same mechanism as `installPaintSurfaceSerializer`, but:
 *   - Source canvas is the RAW (resolved via the caller-supplied callback),
 *     not `this._element` (which is the EFFECTIVE canvas during live render).
 *   - Invalidation is explicit: the caller must call
 *     `invalidateMaskRawSrcCache(img)` after every write to RAW. Masks don't
 *     follow the `setElement` write convention — `bakeStrokeOntoMask` and
 *     `materializeEffectiveMaskInto` write to RAW directly and call
 *     `setElement` only on EFFECTIVE, so the paint-surface auto-invalidation
 *     wouldn't fire.
 *
 * Idempotent — guarded by `__fiasMaskRawSrcCache`.
 */

const MASK_INSTALL_FLAG = '__fiasMaskRawSrcCache';
const MASK_CACHE_KEY = '__fiasMaskRawSrc';

type MaskCached = fabric.FabricImage & {
  [MASK_INSTALL_FLAG]?: boolean;
  [MASK_CACHE_KEY]?: string | undefined;
};

export function installMaskRawSrcCache(
  img: fabric.FabricImage,
  getRawCanvas: (img: fabric.FabricImage) => HTMLCanvasElement | null,
): void {
  const tagged = img as MaskCached;
  if (tagged[MASK_INSTALL_FLAG]) return;

  const baseToObject = img.toObject.bind(img) as (
    propertiesToInclude?: string[],
  ) => Record<string, unknown>;
  const baseGetSrc = img.getSrc.bind(img) as (filtered?: boolean) => string;

  img.getSrc = function (this: fabric.FabricImage, filtered?: boolean): string {
    if (filtered) return baseGetSrc(filtered);
    const self = this as MaskCached;
    const cached = self[MASK_CACHE_KEY];
    if (cached !== undefined) return cached;
    const raw = getRawCanvas(self);
    // No raw attached yet (e.g. a mask under rehydration race). Fall back to
    // the base behavior — Fabric will encode whatever `_element` is, which is
    // the EFFECTIVE canvas. That's the same lossy fallback the swap-element
    // version had when `readRawMaskCanvas` returned null, so we preserve
    // existing behavior without caching the wrong bytes.
    if (!raw) return baseGetSrc(filtered);
    const fresh = raw.toDataURL();
    self[MASK_CACHE_KEY] = fresh;
    return fresh;
  } as typeof img.getSrc;

  // toObject override forwards directly to the base implementation. The
  // src field it populates now comes from our cached getSrc above. We keep
  // the wrapper indirection (instead of letting base toObject run unwrapped)
  // so the install flag remains the single source of truth — any future
  // toObject-level transformation lands in one place.
  img.toObject = function (
    this: fabric.FabricImage,
    propertiesToInclude?: string[],
  ): Record<string, unknown> {
    return baseToObject(propertiesToInclude);
  } as typeof img.toObject;

  tagged[MASK_INSTALL_FLAG] = true;
}

/** Clear the cached RAW dataURL. Call this after every write to the raw mask
 *  canvas (`bakeStrokeOntoMask` and the materialize-effective path). */
export function invalidateMaskRawSrcCache(img: fabric.FabricImage): void {
  (img as MaskCached)[MASK_CACHE_KEY] = undefined;
}
